import { Link } from 'react-router-dom';
import { ArrowRight, Users, Home as HomeIcon, Building2, TrendingUp, Target, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { AdvertiseHero } from '@/components/advertise/AdvertiseHero';
import { AdvertiseBenefitsGrid } from '@/components/advertise/AdvertiseBenefitsGrid';
import { AdvertiseComparisonTable } from '@/components/advertise/AdvertiseComparisonTable';
import { AdvertiseFAQ } from '@/components/advertise/AdvertiseFAQ';
import { ADVERTISE_HUB } from '@/constants/testIds/advertise';

const TRACKS = [
  {
    id: 'agents', icon: Users, eyebrow: 'For real estate agents',
    title: 'Membership & Listing Boost', desc: 'Sort-to-top placement, retargeting ads, and vetted buyer leads for agents and brokerages.',
    to: '/advertise/agents', cta: 'Explore for agents', testId: ADVERTISE_HUB.agentsCta,
  },
  {
    id: 'sellers', icon: HomeIcon, eyebrow: 'For home sellers',
    title: 'Single Listing Boost', desc: 'Boost your own listing with premium placement and retargeting — no agent required.',
    to: '/advertise/sellers', cta: 'Boost my listing', testId: ADVERTISE_HUB.sellersCta,
  },
  {
    id: 'builders', icon: Building2, eyebrow: 'For builders',
    title: 'Community Boost', desc: 'Put new-construction communities in front of buyers actively searching your markets.',
    to: '/advertise/builders', cta: 'Explore for builders', testId: ADVERTISE_HUB.buildersCta,
  },
];

const BENEFITS = [
  { icon: TrendingUp, title: 'More qualified leads', desc: 'Boosted placements convert up to 3x more buyer inquiries than organic listings.' },
  { icon: Target, title: 'Hyper-local targeting', desc: 'Reach buyers by city, price band, and property type at the exact moment they search.' },
  { icon: BarChart3, title: 'Transparent analytics', desc: 'Track impressions, clicks, saves, and leads from a real-time dashboard.' },
];

const FAQS = [
  ['What\u2019s the difference between the three options?', 'Agent Membership is an ongoing subscription for agents and brokerages managing many listings. Listing Boost is a one-time purchase any home seller can buy to promote a single property. Community Boost is built for builders marketing new-construction communities.'],
  ['How are boosted listings labeled?', 'Every boosted placement is clearly marked so buyers always know it\u2019s promoted — we never hide or misrepresent sponsored content.'],
  ['Can I switch between plans later?', 'Yes. Agents can upgrade, downgrade, or cancel a membership anytime from their dashboard. Boost purchases are one-time and don\u2019t auto-renew.'],
  ['How fast will I hear back after I submit a request?', 'Agent and builder requests are reviewed within 1 business day. Listing Boost purchases activate immediately after checkout.'],
];

export default function AdvertiseHub() {
  const revealRef = useScrollReveal();
  return (
    <div ref={revealRef}>
      <AdvertiseHero
        eyebrow="Advertise on casahom"
        title="Power your real estate marketing with casahom"
        subtitle="Reach ready-to-move buyers with the only marketplace built to market and sell the home — whichever seat you\u2019re in."
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="reveal grid grid-cols-1 gap-6 lg:grid-cols-3">
          {TRACKS.map((t) => (
            <div key={t.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-blue-50 text-blue-600"><t.icon size={22} /></div>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-blue-600">{t.eyebrow}</p>
              <h3 className="mt-1 font-display text-xl font-semibold text-slate-900">{t.title}</h3>
              <p className="mt-2 flex-1 text-sm text-slate-600">{t.desc}</p>
              <Button asChild className="mt-5 rounded-full" data-testid={t.testId}>
                <Link to={t.to}>{t.cta}<ArrowRight size={16} className="ml-1" /></Link>
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-sky-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center">
            <h2 className="font-display text-2xl font-semibold text-slate-900 sm:text-3xl">Why casahom</h2>
            <p className="mt-1 text-slate-600">Built for every side of the transaction.</p>
          </div>
          <div className="mt-8"><AdvertiseBenefitsGrid items={BENEFITS} /></div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="reveal text-center">
          <h2 className="font-display text-2xl font-semibold text-slate-900 sm:text-3xl">Compare all options</h2>
          <p className="mt-1 text-slate-600">Pick the path that matches how you sell.</p>
        </div>
        <div className="mt-8"><AdvertiseComparisonTable /></div>
      </section>

      <AdvertiseFAQ items={FAQS} />
    </div>
  );
}
